import { useEffect, useMemo, useState } from 'react';
import { TFunction } from 'i18next';
import {
    CompoundInterestParams,
    Currency,
    DepositFrequency,
    ScenarioType,
    TaxRate,
} from '../../../../utils/compound-interest/types';
import {
    CUSTOM_SCENARIO,
    MARKET_SCENARIOS,
    getCurrencyConversionRate,
    getSliderRangesByCurrency,
    roundCurrencyAmount,
} from '../../../../utils/compound-interest/constants';

interface UseCompoundInterestCalculatorParams {
    params: CompoundInterestParams;
    onChange: (params: CompoundInterestParams) => void;
    t: TFunction;
}

const getDynamicSliderMax = (value: number, sliderMax: number, step: number): number => {
    if (value <= sliderMax) {
        return sliderMax;
    }

    return Math.ceil(value / step) * step;
};

export const useCompoundInterestCalculator = ({ params, onChange, t }: UseCompoundInterestCalculatorParams) => {
    const sliderRanges = useMemo(() => getSliderRangesByCurrency(params.currency), [params.currency]);

    const initialDepositSliderMax = getDynamicSliderMax(
        params.initialDeposit,
        sliderRanges.initialDeposit.max,
        sliderRanges.initialDeposit.step,
    );
    const regularDepositSliderMax = getDynamicSliderMax(
        params.regularDeposit,
        sliderRanges.regularDeposit.max,
        sliderRanges.regularDeposit.step,
    );

    const activeScenario: ScenarioType = useMemo(() => {
        const matchingScenario = MARKET_SCENARIOS.find(
            (scenario) =>
                scenario.interestRate === params.interestRate && scenario.inflationRate === params.inflationRate,
        );

        return matchingScenario?.id || 'custom';
    }, [params.inflationRate, params.interestRate]);

    const [customScenarioValues, setCustomScenarioValues] = useState({
        interestRate: CUSTOM_SCENARIO.interestRate,
        inflationRate: CUSTOM_SCENARIO.inflationRate,
    });

    useEffect(() => {
        if (activeScenario !== 'custom') {
            return;
        }

        setCustomScenarioValues({
            interestRate: params.interestRate,
            inflationRate: params.inflationRate,
        });
    }, [activeScenario, params.inflationRate, params.interestRate]);

    const scenarioIcons: Record<ScenarioType, string> = {
        optimistic: '🚀',
        neutral: '⚖️',
        pessimistic: '🛡️',
        custom: '⚙️',
    };

    const scenarioLabels: Record<ScenarioType, string> = {
        optimistic: t('pages.compoundInterest.scenarios.optimisticShort'),
        neutral: t('pages.compoundInterest.scenarios.neutralShort'),
        pessimistic: t('pages.compoundInterest.scenarios.pessimisticShort'),
        custom: t('pages.compoundInterest.scenarios.custom'),
    };

    const customScenarioTab = {
        id: 'custom' as ScenarioType,
        interestRate: activeScenario === 'custom' ? params.interestRate : customScenarioValues.interestRate,
        inflationRate: activeScenario === 'custom' ? params.inflationRate : customScenarioValues.inflationRate,
    };

    const allTabs = [
        ...MARKET_SCENARIOS.map((scenario) => ({
            id: scenario.id,
            interestRate: scenario.interestRate,
            inflationRate: scenario.inflationRate,
        })),
        customScenarioTab,
    ];

    const handleChange = <K extends keyof CompoundInterestParams>(field: K, value: CompoundInterestParams[K]) => {
        onChange({ ...params, [field]: value });
    };

    const handleNumberChange = (field: keyof CompoundInterestParams, value: string) => {
        const parsed = parseFloat(value);
        if (Number.isNaN(parsed)) {
            return;
        }

        const range = sliderRanges[field as keyof typeof sliderRanges];
        if (!range) {
            handleChange(field, Math.max(parsed, 0) as CompoundInterestParams[typeof field]);
            return;
        }

        if (field === 'initialDeposit' || field === 'regularDeposit') {
            handleChange(field, Math.max(parsed, range.min) as CompoundInterestParams[typeof field]);
            return;
        }

        handleChange(field, Math.min(Math.max(parsed, range.min), range.max) as CompoundInterestParams[typeof field]);
    };

    const handleScenarioChange = (scenarioId: ScenarioType) => {
        if (scenarioId === 'custom') {
            onChange({
                ...params,
                interestRate: customScenarioValues.interestRate,
                inflationRate: customScenarioValues.inflationRate,
            });
            return;
        }

        const scenario = MARKET_SCENARIOS.find((marketScenario) => marketScenario.id === scenarioId);
        if (!scenario) {
            return;
        }

        onChange({
            ...params,
            interestRate: scenario.interestRate,
            inflationRate: scenario.inflationRate,
        });
    };

    const handleCurrencyChange = (nextCurrency: Currency) => {
        if (nextCurrency === params.currency) {
            return;
        }

        const conversionRate = getCurrencyConversionRate(params.currency, nextCurrency);

        onChange({
            ...params,
            currency: nextCurrency,
            initialDeposit: roundCurrencyAmount(params.initialDeposit * conversionRate, nextCurrency),
            regularDeposit: roundCurrencyAmount(params.regularDeposit * conversionRate, nextCurrency),
        });
    };

    const isTaxExemptionByTestEnabled = params.taxRate === 0;

    const handleTaxExemptionByTestChange = () => {
        onChange({
            ...params,
            taxRate: (isTaxExemptionByTestEnabled ? 15 : 0) as TaxRate,
        });
    };

    const handleDepositFrequencyChange = (frequency: DepositFrequency) => {
        handleChange('depositFrequency', frequency);
    };

    return {
        activeScenario,
        allTabs,
        initialDepositSliderMax,
        isTaxExemptionByTestEnabled,
        regularDepositSliderMax,
        scenarioIcons,
        scenarioLabels,
        sliderRanges,
        handleChange,
        handleCurrencyChange,
        handleDepositFrequencyChange,
        handleNumberChange,
        handleScenarioChange,
        handleTaxExemptionByTestChange,
    };
};
